import { alterarSenhaSchema } from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto } from '@mymaxx/ui';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ErroApi, authApi } from '../lib/api';
import { useSessao } from '../lib/sessao';

/**
 * Troca obrigatória da senha provisória.
 *
 * Quem cai aqui foi criado por importação de planilha: a senha atual foi definida pelo
 * admin e é a mesma para todo mundo do arquivo. Enquanto ela valer, qualquer um com a
 * planilha na mão é esta pessoa — por isso o painel não abre antes da troca, nem para
 * configurar o autenticador.
 */
export function TrocarSenha() {
  const definir = useSessao((s) => s.definir);
  const navegar = useNavigate();
  const nome = useSessao((s) => s.usuario?.nome.split(' ')[0]);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const parse = alterarSenhaSchema.safeParse({ senhaAtual, novaSenha, confirmacao });
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      // A resposta já vem com a sessão nova e sem a marca de provisória: guardar aqui é o
      // que destrava o painel, sem precisar entrar de novo.
      definir(await authApi.alterarSenha(parse.data));
      // E sair daqui na mão, porque a regra de rota só empurra PARA esta tela: sem isto a
      // pessoa troca a senha e continua olhando para o mesmo formulário, sem erro nenhum.
      navegar('/', { replace: true });
    } catch (e2) {
      setErro(e2 instanceof ErroApi ? e2.mensagem : 'Não consegui trocar a senha');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-5 px-6 py-10">
      <div>
        <h1 className="text-xl font-semibold">{nome ? `${nome}, troque` : 'Troque'} sua senha</h1>
        <p className="mt-1 text-sm text-neutral-500">
          A senha que você recebeu é provisória e foi definida por outra pessoa. Escolha uma sua
          para entrar no painel.
        </p>
      </div>

      <form className="space-y-4" onSubmit={(e) => void enviar(e)} noValidate>
        <CampoTexto
          rotulo="Senha provisória"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={senhaAtual}
          onChange={(e) => setSenhaAtual(e.target.value)}
        />
        <CampoTexto
          rotulo="Nova senha"
          type="password"
          autoComplete="new-password"
          value={novaSenha}
          onChange={(e) => setNovaSenha(e.target.value)}
          ajuda="Pelo menos 10 caracteres. Evite algo que já use em outro lugar."
        />
        <CampoTexto
          rotulo="Repita a nova senha"
          type="password"
          autoComplete="new-password"
          value={confirmacao}
          onChange={(e) => setConfirmacao(e.target.value)}
        />
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <Botao type="submit" className="w-full" carregando={enviando}>
          Trocar senha e entrar
        </Botao>
      </form>
    </main>
  );
}
